import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";

export default function RegisterPage() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization_name: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await register(form);
      toast.success("Account created");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md fade-up">
        <div className="mb-10">
          <div className="flex items-center gap-2.5 mb-8">
            <ChurchFlowLogo size={44} className="soft-glow rounded-2xl" />
            <div className="font-serif text-2xl tracking-tight">ChurchFlow</div>
          </div>
          <h1 className="font-serif text-5xl leading-[1.05] tracking-tight">Start your ledger.</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Free to begin. Set up your first organization in seconds.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">Your name</Label>
            <Input id="name" data-testid="register-name-input" value={form.name} onChange={upd("name")} required className="h-12 rounded-2xl bg-card" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="organization_name" className="text-xs uppercase tracking-wider text-muted-foreground">Organization name</Label>
            <Input id="organization_name" data-testid="register-org-input" value={form.organization_name} onChange={upd("organization_name")} placeholder="e.g. Grace Community Church" className="h-12 rounded-2xl bg-card" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input id="email" data-testid="register-email-input" type="email" value={form.email} onChange={upd("email")} required className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
            <Input id="password" data-testid="register-password-input" type="password" value={form.password} onChange={upd("password")} required minLength={6} className="h-12 rounded-2xl bg-card" />
            <p className="text-xs text-muted-foreground">At least 6 characters.</p>
          </div>
          <Button type="submit" data-testid="register-submit-button" disabled={submitting} className="w-full h-12 rounded-2xl soft-glow active:scale-[0.98] transition-transform">
            {submitting ? "Creating account…" : "Create account"}
          </Button>
          <p className="text-sm text-muted-foreground text-center pt-2">
            Already have an account?{" "}
            <Link to="/login" data-testid="goto-login-link" className="text-primary underline-offset-4 hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </form>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
